import type {
  Milestone,
  Project,
  ProjectMember,
  ProjectSnapshot,
  StatusHistoryEntry,
  UserProfile,
  WorkItem,
  WorkItemDependency,
  WorkItemLink,
  WorkspaceData,
} from "../types/domain";

const now = "2026-08-02T14:00:00.000Z";
const acceptedAt = "2026-08-02T13:07:13.000Z";

const owner: UserProfile = {
  id: "demo-owner",
  displayName: "Jeremy David Sanchez",
  handle: "rasenur",
  avatarUrl: null,
};

const projects: Project[] = [
  {
    id: "eqe",
    slug: "eremos-quant-engine",
    name: "Eremos Quant Engine",
    summary:
      "Infraestructura personal de investigación cuantitativa histórica, causal, reproducible y auditable.",
    vision:
      "Construir un laboratorio disciplinado que analice, recuerde y aprenda bajo límites externos, sin confundir reproducibilidad con rentabilidad.",
    accent: "#22d3c5",
    status: "paused",
    createdBy: owner.id,
    roadmapDisclaimer:
      "Progreso calculado sobre el roadmap conocido actualmente; no representa la totalidad absoluta de trabajo futuro.",
    createdAt: "2026-07-29T12:00:00.000Z",
    updatedAt: now,
  },
  {
    id: "dashboard",
    slug: "eremos-project-dashboard",
    name: "Eremos Project Dashboard",
    summary:
      "Aplicación visual multi-proyecto para avance, evidencia, dependencias y continuidad técnica del ecosistema EremosHub.",
    vision:
      "Convertir el estado real de cada proyecto en una representación clara, verificable y compartible sin depender de SaaS externos.",
    accent: "#f7b955",
    status: "active",
    createdBy: owner.id,
    roadmapDisclaimer:
      "El porcentaje representa únicamente el alcance conocido y versionado del MVP actual.",
    createdAt: "2026-08-02T13:20:00.000Z",
    updatedAt: now,
  },
];

let order = 0;
function item(
  id: string,
  projectId: string,
  parentId: string | null,
  itemType: WorkItem["itemType"],
  title: string,
  status: WorkItem["status"],
  weight: number,
  description = "",
  focus = false,
): WorkItem {
  order += 10;
  return {
    id,
    projectId,
    parentId,
    itemType,
    title,
    description,
    status,
    weight,
    sortOrder: order,
    depth: itemType === "phase" ? 0 : itemType === "block" ? 1 : itemType === "task" ? 2 : 3,
    isCurrentFocus: focus,
    startedAt: status === "pending" || status === "planned" ? null : "2026-07-29T12:00:00.000Z",
    targetDate: null,
    acceptedAt: status === "accepted" ? acceptedAt : null,
    metadata: {},
    createdAt: "2026-08-02T13:20:00.000Z",
    updatedAt: now,
  };
}

order = 0;
const quantItems: WorkItem[] = [
  item("eqe-foundation", "eqe", null, "phase", "Fundación operativa", "accepted", 1),
  item(
    "eqe-pr1",
    "eqe",
    "eqe-foundation",
    "block",
    "PR #1 · Gobierno del repositorio",
    "accepted",
    2,
    "README autoritativo, CI y disciplina de cambios.",
  ),
  item(
    "eqe-pr2",
    "eqe",
    "eqe-foundation",
    "block",
    "PR #2 · Configuración y PostgreSQL local",
    "accepted",
    2,
  ),
  item(
    "eqe-pr3",
    "eqe",
    "eqe-foundation",
    "block",
    "PR #3 · Migraciones y estado operativo",
    "accepted",
    2,
  ),
  item("eqe-pr4", "eqe", "eqe-foundation", "block", "PR #4 · FastAPI operativo", "accepted", 2),

  item("eqe-data", "eqe", null, "phase", "Datos históricos verificables", "accepted", 1),
  item(
    "eqe-pr5",
    "eqe",
    "eqe-data",
    "block",
    "PR #5 · Contratos canónicos de mercado",
    "accepted",
    3,
  ),
  item("eqe-pr6", "eqe", "eqe-data", "block", "PR #6 · Parquet atómico", "accepted", 3),
  item("eqe-pr7", "eqe", "eqe-data", "block", "PR #7 · Catálogo DuckDB", "accepted", 3),
  item(
    "eqe-pr8",
    "eqe",
    "eqe-data",
    "block",
    "PR #8 · Ingesta histórica controlada",
    "accepted",
    4,
  ),
  item(
    "eqe-pr9",
    "eqe",
    "eqe-data",
    "block",
    "PR #9 · Dataset releases reproducibles",
    "accepted",
    4,
  ),

  item("eqe-replay", "eqe", null, "phase", "Replay causal y evidencia", "in_development", 1),
  item(
    "eqe-pr10",
    "eqe",
    "eqe-replay",
    "block",
    "PR #10 · Reloj y eventos causales",
    "accepted",
    4,
  ),
  item(
    "eqe-pr11",
    "eqe",
    "eqe-replay",
    "block",
    "PR #11 · Historical Replay Engine",
    "accepted",
    5,
  ),
  item(
    "eqe-pr12",
    "eqe",
    "eqe-replay",
    "block",
    "PR #12 · Persistencia de replay evidence",
    "accepted",
    5,
  ),
  item(
    "eqe-pr13",
    "eqe",
    "eqe-replay",
    "block",
    "PR #13 · Catálogo CLI de evidencia",
    "accepted",
    3,
  ),
  item(
    "eqe-pr14",
    "eqe",
    "eqe-replay",
    "block",
    "PR #14 · Índice PostgreSQL append-only",
    "accepted",
    4,
  ),
  item(
    "eqe-pr15",
    "eqe",
    "eqe-replay",
    "block",
    "PR #15 · Backup y restauración portátil",
    "accepted",
    5,
  ),
  item(
    "eqe-replay-config",
    "eqe",
    "eqe-replay",
    "block",
    "ReplayConfiguration",
    "in_development",
    1,
    "Contrato base creado y validado localmente; integración pausada antes de alterar la identidad de run.",
    true,
  ),
  item(
    "eqe-config-contract",
    "eqe",
    "eqe-replay-config",
    "task",
    "Contrato base inmutable",
    "accepted",
    2,
  ),
  item(
    "eqe-config-runtime-tests",
    "eqe",
    "eqe-replay-config",
    "task",
    "Pruebas negativas y límites extremos",
    "planned",
    2,
  ),
  item(
    "eqe-config-serialization",
    "eqe",
    "eqe-replay-config",
    "task",
    "Serialización y parser versionados",
    "planned",
    3,
  ),
  item(
    "eqe-config-release",
    "eqe",
    "eqe-replay-config",
    "task",
    "Verificación contra DatasetRelease",
    "planned",
    3,
  ),
  item(
    "eqe-config-run-id",
    "eqe",
    "eqe-replay-config",
    "task",
    "Version boundary e integración con run_id",
    "planned",
    5,
  ),
  item(
    "eqe-config-evidence",
    "eqe",
    "eqe-replay-config",
    "task",
    "Propagación a trace y manifest",
    "planned",
    4,
  ),
  item(
    "eqe-config-pr",
    "eqe",
    "eqe-replay-config",
    "task",
    "Suite completa, PR, CI y aceptación",
    "planned",
    3,
  ),

  item("eqe-pause", "eqe", null, "phase", "Pausa controlada", "in_development", 1),
  item(
    "eqe-dashboard-pause",
    "eqe",
    "eqe-pause",
    "block",
    "Construir Eremos Project Dashboard",
    "in_development",
    4,
    "Proyecto auxiliar independiente que debe completar el MVP antes de volver a ReplayConfiguration.",
    true,
  ),

  item("eqe-future", "eqe", null, "phase", "Bloques posteriores", "planned", 1),
  item("eqe-checkpoints", "eqe", "eqe-future", "block", "Replay checkpoints", "planned", 3),
  item(
    "eqe-dataset-roles",
    "eqe",
    "eqe-future",
    "block",
    "Metadata ampliada de roles temporales",
    "planned",
    3,
  ),
  item("eqe-features", "eqe", "eqe-future", "block", "Feature pipeline", "planned", 5),
  item("eqe-strategies", "eqe", "eqe-future", "block", "Estrategias y baselines", "planned", 5),
  item("eqe-paper", "eqe", "eqe-future", "block", "Paper broker", "planned", 5),
  item("eqe-portfolio", "eqe", "eqe-future", "block", "Portfolio", "planned", 4),
  item("eqe-risk", "eqe", "eqe-future", "block", "Risk engine independiente", "planned", 5),
  item("eqe-models", "eqe", "eqe-future", "block", "Modelos y Champion/Challenger", "deferred", 6),
  item("eqe-testnet", "eqe", "eqe-future", "block", "Binance Testnet", "out_of_scope", 6),
  item("eqe-live", "eqe", "eqe-future", "block", "Spot real mínimo", "out_of_scope", 10),
];

order = 0;
const dashboardItems: WorkItem[] = [
  item("dash-d1", "dashboard", null, "phase", "D1 · Fundación", "accepted", 1),
  item("dash-stack", "dashboard", "dash-d1", "block", "React, TypeScript y Vite", "accepted", 4),
  item(
    "dash-design",
    "dashboard",
    "dash-d1",
    "block",
    "Sistema visual y responsive",
    "accepted",
    4,
  ),
  item(
    "dash-quality",
    "dashboard",
    "dash-d1",
    "block",
    "Biome, TypeScript, Vitest y CI",
    "accepted",
    3,
  ),

  item("dash-d2", "dashboard", null, "phase", "D2 · Supabase y seguridad", "in_verification", 1),
  item("dash-schema", "dashboard", "dash-d2", "block", "Modelo multi-proyecto", "accepted", 5),
  item(
    "dash-rls",
    "dashboard",
    "dash-d2",
    "block",
    "RLS owner/editor/viewer",
    "in_verification",
    5,
    "Migraciones listas; pendiente aplicar al proyecto Supabase independiente.",
  ),
  item(
    "dash-history",
    "dashboard",
    "dash-d2",
    "block",
    "Historial y snapshots append-only",
    "accepted",
    4,
  ),

  item("dash-d3", "dashboard", null, "phase", "D3 · Experiencia visual", "accepted", 1),
  item("dash-overview", "dashboard", "dash-d3", "block", "Portfolio y overview", "accepted", 3),
  item("dash-roadmap", "dashboard", "dash-d3", "block", "Jerarquía y board", "accepted", 4),
  item("dash-graph", "dashboard", "dash-d3", "block", "Mapa React Flow", "accepted", 3),
  item("dash-timeline", "dashboard", "dash-d3", "block", "Timeline y evidencia", "accepted", 3),
  item("dash-members", "dashboard", "dash-d3", "block", "Sharing por roles", "accepted", 3),

  item("dash-d4", "dashboard", null, "phase", "D4 · Roadmap inicial", "accepted", 1),
  item(
    "dash-seed-eqe",
    "dashboard",
    "dash-d4",
    "block",
    "Carga Eremos Quant Engine",
    "accepted",
    5,
  ),
  item(
    "dash-seed-self",
    "dashboard",
    "dash-d4",
    "block",
    "Carga del propio dashboard",
    "accepted",
    3,
  ),

  item("dash-d5", "dashboard", null, "phase", "D5 · Publicación", "in_verification", 1),
  item(
    "dash-cloudflare",
    "dashboard",
    "dash-d5",
    "block",
    "Cloudflare Pages",
    "planned",
    4,
    "Build y guía listos; requiere conectar el repositorio desde la cuenta Cloudflare.",
  ),
  item("dash-domain", "dashboard", "dash-d5", "block", "dashboard.eremoshub.com", "planned", 3),
  item(
    "dash-acceptance",
    "dashboard",
    "dash-d5",
    "block",
    "Aceptación con usuarios reales",
    "in_verification",
    4,
    "Requiere proyecto Supabase y pruebas con owner/editor/viewer.",
    true,
  ),
];

const workItems = [...quantItems, ...dashboardItems];

const dependencies: WorkItemDependency[] = [
  {
    projectId: "eqe",
    workItemId: "eqe-config-run-id",
    dependsOnWorkItemId: "eqe-config-serialization",
    dependencyType: "blocks",
  },
  {
    projectId: "eqe",
    workItemId: "eqe-config-evidence",
    dependsOnWorkItemId: "eqe-config-run-id",
    dependencyType: "blocks",
  },
  {
    projectId: "eqe",
    workItemId: "eqe-config-pr",
    dependsOnWorkItemId: "eqe-config-evidence",
    dependencyType: "blocks",
  },
  {
    projectId: "dashboard",
    workItemId: "dash-cloudflare",
    dependsOnWorkItemId: "dash-rls",
    dependencyType: "blocks",
  },
  {
    projectId: "dashboard",
    workItemId: "dash-domain",
    dependsOnWorkItemId: "dash-cloudflare",
    dependencyType: "blocks",
  },
];

const prLinks = Array.from({ length: 15 }, (_, index): WorkItemLink => {
  const pr = index + 1;
  return {
    id: `link-pr-${pr}`,
    projectId: "eqe",
    workItemId: `eqe-pr${pr}`,
    milestoneId: null,
    linkType: "pull_request",
    label: `Pull Request #${pr}`,
    url: `https://github.com/Rasenur/eremos-quant-engine/pull/${pr}`,
    externalId: String(pr),
    metadata: {},
    createdAt: now,
  };
});

const links: WorkItemLink[] = [
  ...prLinks,
  {
    id: "link-eqe-repo",
    projectId: "eqe",
    workItemId: null,
    milestoneId: null,
    linkType: "repository",
    label: "Repositorio principal",
    url: "https://github.com/Rasenur/eremos-quant-engine",
    externalId: null,
    metadata: {},
    createdAt: now,
  },
  {
    id: "link-config-branch",
    projectId: "eqe",
    workItemId: "eqe-replay-config",
    milestoneId: null,
    linkType: "branch",
    label: "feature/replay-configuration-contract",
    url: "https://github.com/Rasenur/eremos-quant-engine/tree/feature/replay-configuration-contract",
    externalId: "feature/replay-configuration-contract",
    metadata: {},
    createdAt: now,
  },
  {
    id: "link-config-commit",
    projectId: "eqe",
    workItemId: "eqe-config-contract",
    milestoneId: null,
    linkType: "commit",
    label: "e7b9cd0 · contrato base",
    url: "https://github.com/Rasenur/eremos-quant-engine/commit/e7b9cd0d54be180fa4b35f16932c00b92a314878",
    externalId: "e7b9cd0d54be180fa4b35f16932c00b92a314878",
    metadata: {},
    createdAt: now,
  },
  {
    id: "link-dashboard-repo",
    projectId: "dashboard",
    workItemId: null,
    milestoneId: null,
    linkType: "repository",
    label: "Repositorio del dashboard",
    url: "https://github.com/Rasenur/eremos-project-dashboard",
    externalId: null,
    metadata: {},
    createdAt: now,
  },
];

const milestones: Milestone[] = [
  {
    id: "milestone-evidence",
    projectId: "eqe",
    title: "Evidencia portátil aceptada",
    description: "PR #15 preserva bytes exactos de replay evidence y restauración conflict-safe.",
    status: "accepted",
    targetDate: "2026-08-02",
    completedAt: acceptedAt,
    sortOrder: 10,
    weight: 5,
  },
  {
    id: "milestone-config",
    projectId: "eqe",
    title: "ReplayConfiguration integrada",
    description: "Version boundary, run identity, trace, manifest y verificación independiente.",
    status: "planned",
    targetDate: null,
    completedAt: null,
    sortOrder: 20,
    weight: 5,
  },
  {
    id: "milestone-dashboard",
    projectId: "dashboard",
    title: "MVP listo para publicación",
    description:
      "Código, migraciones, CI y documentación preparados para Supabase y Cloudflare Pages.",
    status: "in_verification",
    targetDate: "2026-08-03",
    completedAt: null,
    sortOrder: 10,
    weight: 5,
  },
];

const history: StatusHistoryEntry[] = [
  {
    id: "history-1",
    projectId: "eqe",
    workItemId: "eqe-pr15",
    fromStatus: "in_manual_acceptance",
    toStatus: "accepted",
    reason: "CI, suite completa y aceptación manual aprobadas.",
    changedBy: owner.id,
    changedAt: acceptedAt,
  },
  {
    id: "history-2",
    projectId: "eqe",
    workItemId: "eqe-replay-config",
    fromStatus: "planned",
    toStatus: "in_development",
    reason: "Contrato base creado en rama y validado localmente.",
    changedBy: owner.id,
    changedAt: "2026-08-02T13:32:52.000Z",
  },
  {
    id: "history-3",
    projectId: "eqe",
    workItemId: "eqe-dashboard-pause",
    fromStatus: "planned",
    toStatus: "in_development",
    reason: "Pausa controlada para construir el dashboard multi-proyecto.",
    changedBy: owner.id,
    changedAt: "2026-08-02T13:45:00.000Z",
  },
  {
    id: "history-4",
    projectId: "dashboard",
    workItemId: "dash-design",
    fromStatus: "in_development",
    toStatus: "accepted",
    reason: "Sistema visual responsive y vistas principales completadas.",
    changedBy: owner.id,
    changedAt: now,
  },
];

const snapshots: ProjectSnapshot[] = [
  {
    id: "snapshot-eqe-1",
    projectId: "eqe",
    capturedAt: "2026-07-30T20:30:00.000Z",
    progressPercent: 23.4,
    progressModelVersion: 1,
    itemCount: 18,
    statusCounts: { accepted: 7, planned: 11 },
    phaseProgress: {},
    knownRoadmapItemCount: 18,
    metadata: { label: "Corte v2" },
  },
  {
    id: "snapshot-eqe-2",
    projectId: "eqe",
    capturedAt: "2026-08-01T15:45:00.000Z",
    progressPercent: 45.8,
    progressModelVersion: 1,
    itemCount: 30,
    statusCounts: { accepted: 14, planned: 15, in_development: 1 },
    phaseProgress: {},
    knownRoadmapItemCount: 30,
    metadata: { label: "PR #14" },
  },
  {
    id: "snapshot-eqe-3",
    projectId: "eqe",
    capturedAt: now,
    progressPercent: 52.6,
    progressModelVersion: 1,
    itemCount: quantItems.length,
    statusCounts: { accepted: 16, planned: 14, in_development: 2, deferred: 1, out_of_scope: 2 },
    phaseProgress: {},
    knownRoadmapItemCount: quantItems.length,
    metadata: { label: "Pausa dashboard" },
  },
  {
    id: "snapshot-dashboard-1",
    projectId: "dashboard",
    capturedAt: now,
    progressPercent: 76.2,
    progressModelVersion: 1,
    itemCount: dashboardItems.length,
    statusCounts: { accepted: 12, in_verification: 3, planned: 2 },
    phaseProgress: {},
    knownRoadmapItemCount: dashboardItems.length,
    metadata: { label: "MVP de código" },
  },
];

const members: ProjectMember[] = projects.map((project) => ({
  projectId: project.id,
  userId: owner.id,
  role: "owner",
  joinedAt: project.createdAt,
  profile: owner,
}));

export const initialDemoWorkspace: WorkspaceData = {
  projects,
  members,
  workItems,
  dependencies,
  links,
  milestones,
  history,
  snapshots,
  currentProfile: owner,
};
